/**
 * DependencyContainer.js
 * ----------------------------------------------------------------------------
 * Lightweight dependency injection container for the calendar application.
 *
 * Refactor goals:
 * - Make timeouts configurable (avoid hard-coded 2000ms)
 * - Reduce log noise (use debug for lifecycle chatter)
 * - Preserve error causes for easier debugging
 * - Make singleton caching behavior explicit and predictable
 * - Keep built-in dependencies (dom, plugins) but allow config overrides
 */

export default class DependencyContainer {
  constructor(config, logger, errorHandler) {
    this.config = config;
    this.logger = logger;
    this.errorHandler = errorHandler;

    this.dependencies = new Map(); // cached instances (singletons)
    this.factories = new Map();    // registrations
    this.singletons = new Set();   // names registered as singleton
    this.initialized = false;
  }

  /**
   * Register a factory function for a dependency
   */
  register(name, factory, options = {}) {
    if (!name || typeof name !== 'string') {
      throw new Error('Dependency name must be a non-empty string');
    }
    if (typeof factory !== 'function') {
      throw new Error(`Factory for '${name}' must be a function`);
    }

    const registration = {
      factory,
      singleton: options.singleton !== false,
      dependencies: Array.isArray(options.dependencies) ? options.dependencies : [],
      initialized: false,
      metadata: options.metadata || {},
    };

    this.factories.set(name, registration);

    if (registration.singleton) this.singletons.add(name);
    else this.singletons.delete(name);

    // Reduce noise: registration is usually debug-level.
    this.logger?.debug?.(`Registered dependency: ${name}`);
  }

  /**
   * Get a dependency, creating it if necessary
   */
  async get(name) {
    const registration = this.factories.get(name);
    if (!registration) {
      throw new Error(`Dependency '${name}' not registered`);
    }

    // Only return cached instances for singletons.
    if (registration.singleton && this.dependencies.has(name)) {
      return this.dependencies.get(name);
    }

    try {
      this.logger?.debug?.(`Creating dependency: ${name}`);

      const resolvedDeps = await Promise.all(
        registration.dependencies.map((dep) => this.get(dep))
      );

      const instance = await this._createWithTimeout(
        registration.factory,
        name,
        [this.config, this.logger, ...resolvedDeps]
      );

      if (registration.singleton) {
        this.dependencies.set(name, instance);
      }

      registration.initialized = true;
      this.logger?.debug?.(`Created dependency: ${name}`);

      return instance;
    } catch (error) {
      this.errorHandler?.handleError?.(`Failed to create dependency '${name}'`, error, {
        category: 'di',
        dependency: name,
      });

      // Preserve cause when supported (Node 16+/modern browsers).
      try {
        throw new Error(`Failed to create dependency '${name}': ${error?.message || String(error)}`, {
          cause: error,
        });
      } catch {
        // Fallback for environments without Error cause support
        throw new Error(`Failed to create dependency '${name}': ${error?.message || String(error)}`);
      }
    }
  }

  /**
   * Internal: Create with timeout protection
   */
  _createWithTimeout(factory, name, args) {
    const timeoutMs =
      Number(this.config?.componentTimeoutMs) > 0
        ? Number(this.config.componentTimeoutMs)
        : 2000;

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error(`Timeout creating dependency '${name}' after ${timeoutMs}ms`));
      }, timeoutMs);

      (async () => {
        try {
          const instance = await factory(...args);
          clearTimeout(timeoutId);
          resolve(instance);
        } catch (error) {
          clearTimeout(timeoutId);
          reject(error);
        }
      })();
    });
  }

  /**
   * Get dependency metadata
   */
  getMetadata(name) {
    const reg = this.factories.get(name);
    return reg ? reg.metadata : null;
  }

  /**
   * Initialize the container and core dependencies
   */
  async initialize() {
    if (this.initialized) return;

    try {
      this.logger?.info?.('🔧 Initializing dependency container...');
      this._registerBuiltInDependencies();
      this.initialized = true;
      this.logger?.info?.('✅ Dependency container initialized');
    } catch (error) {
      this.errorHandler?.handleError?.('Dependency container initialization failed', error, {
        category: 'di',
      });
      throw error;
    }
  }

  /**
   * Register built-in dependencies that are always available
   */
  _registerBuiltInDependencies() {
    // DOM utilities
    this.register(
      'dom',
      () => ({
        querySelector: (sel) => document.querySelector(sel),
        querySelectorAll: (sel) => document.querySelectorAll(sel),
        createElement: (tag) => document.createElement(tag),
        getElementById: (id) => document.getElementById(id),
      }),
      {
        singleton: true,
        metadata: { type: 'utility', description: 'DOM manipulation utilities' },
      }
    );

    // Plugin loader (configurable)
    this.register(
      'plugins',
      async (config, logger) => {
        // Allow caller to supply plugins explicitly (already imported plugins)
        if (Array.isArray(config?.fullCalendarPlugins)) {
          logger?.debug?.('Using plugins from config.fullCalendarPlugins');
          return config.fullCalendarPlugins;
        }

        // Default: try load dayGrid plugin (preserves current behavior)
        try {
          const dayGridModule = await import('@fullcalendar/daygrid');
          const dayGridPlugin = dayGridModule.default || dayGridModule;
          logger?.info?.('Loaded FullCalendar plugins');
          return [dayGridPlugin];
        } catch (error) {
          logger?.warn?.('Failed to load FullCalendar plugins:', error?.message || String(error));
          return [];
        }
      },
      {
        singleton: true,
        metadata: { type: 'plugin', description: 'FullCalendar plugin loader' },
      }
    );
  }

  /**
   * Get container statistics
   */
  getStats() {
    return {
      registered: this.factories.size,
      created: this.dependencies.size,
      singletons: this.singletons.size,
      initialized: this.initialized,
      registrations: Array.from(this.factories.keys()).map((name) => {
        const reg = this.factories.get(name);
        return { name, singleton: reg.singleton, metadata: reg.metadata };
      }),
    };
  }

  /**
   * Destroy all dependencies and clean up
   */
  async destroy() {
    this.logger?.info?.('🗑️ Destroying dependency container...');

    const promises = [];
    for (const [name, instance] of this.dependencies) {
      if (instance && typeof instance.destroy === 'function') {
        promises.push(
          this._destroyWithTimeout(instance, name).catch((err) =>
            this.errorHandler?.handleError?.(`Error destroying ${name}`, err, {
              category: 'di',
              dependency: name,
              action: 'destroy',
            })
          )
        );
      }
    }

    await Promise.all(promises);

    this.dependencies.clear();
    this.factories.clear();
    this.singletons.clear();
    this.initialized = false;

    this.logger?.info?.('✅ Dependency container destroyed');
  }

  /**
   * Internal: Destroy with timeout protection
   */
  _destroyWithTimeout(instance, name) {
    const timeoutMs =
      Number(this.config?.destroyTimeoutMs) > 0
        ? Number(this.config.destroyTimeoutMs)
        : Number(this.config?.componentTimeoutMs) > 0
          ? Number(this.config.componentTimeoutMs)
          : 2000;

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error(`Timeout destroying '${name}' after ${timeoutMs}ms`));
      }, timeoutMs);

      (async () => {
        try {
          await instance.destroy();
          clearTimeout(timeoutId);
          resolve();
        } catch (error) {
          clearTimeout(timeoutId);
          reject(error);
        }
      })();
    });
  }
}
